import { useMemo, useState } from "react";
import useSWR from "swr";
import { ActionIcon, Alert, Anchor, Button, Group, Modal, Select, Stack, Text, TextInput, Tooltip } from "@mantine/core";
import ChevronRight from "lucide-react/dist/esm/icons/chevron-right";
import { Channel, Platform, PLATFORM_YOUTUBE, PLATFORM_VK, Playlist, getSubjects, parseApiError, playlistUrl, updatePlaylist, useFetch } from "../api";
import { Panel } from "./Panel";
import { isAdmin } from "../AuthContext";
import { PLATFORM_TITLES, usePlatformLogin } from "../platformLogin";

type SyncStatus = { code: number; message: string } | null;

function channelUrl(platform: Platform, id: string): string {
  if (platform === PLATFORM_YOUTUBE) return `https://www.youtube.com/channel/${id}`;
  if (platform === PLATFORM_VK) return `https://vk.com/club${id}`;
  return "";
}

export const ChannelDetail = ({ channel, platform, onSync, onChanged }: { channel: Channel; platform: Platform; onSync?: () => Promise<void>; onChanged?: () => void }) => {
  const { fetch } = useFetch();
  const [filter, setFilter] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState<SyncStatus>(null);
  const [editing, setEditing] = useState<Playlist | null>(null);
  const { value: isAdminValue } = isAdmin();
  // Checked up front so an expired session shows "войти" instead of failing on click.
  const login = usePlatformLogin(platform, !!onSync && !!isAdminValue);
  const { data: subjects } = useSWR(isAdminValue ? "admin/subjects" : null, () => getSubjects({ fetch }));

  async function handleSync() {
    if (!onSync) return;
    setSyncing(true); setStatus(null);
    try {
      await onSync();
      setStatus({ code: 200, message: "Плейлисты обновлены." });
    } catch (e: any) {
      const err = parseApiError(e);
      const code = err.status || 500;
      if (code === 401) login.check();
      setStatus({ code, message: code === 401 ? `Нет авторизации в ${PLATFORM_TITLES[platform]}.` : code === 404 ? "Канала нет в конфиге." : `Ошибка (${code}): ${err.message}` });
    } finally { setSyncing(false); }
  }

  const playlists = useMemo(() => channel.playlists.filter(p => p.title.toLowerCase().includes(filter.toLowerCase())), [channel.playlists, filter]);
  const chUrl = channelUrl(platform, channel.id);
  const channelName = chUrl ? <Anchor href={chUrl} target="_blank" rel="noopener noreferrer">{channel.description}</Anchor> : channel.description;

  const title = (
    <>
      {channelName}
      {onSync && isAdminValue && (
        <>
          <Text span c="dimmed" fw={400} style={{ marginInline: 10 }}>•</Text>
          {login.ok === false
            ? <Anchor component="button" type="button" c="orange" onClick={login.login}>Войти в {PLATFORM_TITLES[platform]}</Anchor>
            : <Anchor component="button" type="button" c="dimmed" onClick={handleSync} disabled={syncing || login.ok === null}>{syncing ? "Обновление…" : "Обновить плейлисты"}</Anchor>}
        </>
      )}
    </>
  );

  return (
    <Panel title={title} defaultCollapsed headerRight={status && <Text fz="sm" c={status.code === 200 ? "green" : "red"}>{status.message}</Text>}>
      <Stack gap="sm">
        <Text fw={500} fz="md">Плейлисты ({channel.playlists.length})</Text>
        <TextInput size="sm" placeholder="Поиск..." value={filter} onChange={e => setFilter(e.target.value)} />
        <Stack gap={4}>
          {playlists.map(pls => {
            const url = playlistUrl(platform, channel.id, pls.playlist_id);
            return (
              <Group key={pls.playlist_id} gap={4} wrap="nowrap">
                {url ? <Anchor href={url} target="_blank" rel="noopener noreferrer" fz="md" truncate>{pls.title}</Anchor> : <Text fz="md" truncate>{pls.title}</Text>}
                {/* Right after the title, so it's next to what it opens and easy to hit. */}
                {isAdminValue && (
                  <Tooltip label="Название и предмет" openDelay={300} withArrow>
                    <ActionIcon variant="subtle" color="gray" size="md" onClick={() => setEditing(pls)} aria-label="Название и предмет" style={{ flexShrink: 0 }}>
                      <ChevronRight size={18} color="var(--mantine-color-dimmed)" />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            );
          })}
          {playlists.length === 0 && <Text fz="sm" c="dimmed">Ничего не найдено</Text>}
        </Stack>
      </Stack>
      {editing && (
        <PlaylistModal
          platform={platform} channelId={channel.id} playlist={editing} subjects={subjects ?? []}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged?.(); }}
        />
      )}
    </Panel>
  );
};

function PlaylistModal({ platform, channelId, playlist, subjects, onClose, onSaved }: {
  platform: Platform; channelId: string; playlist: Playlist; subjects: { id: number; title: string; playlists?: { platform: Platform }[] }[];
  onClose: () => void; onSaved: () => void;
}) {
  const { fetch } = useFetch();
  const [title, setTitle] = useState(playlist.title);
  const [subjectId, setSubjectId] = useState<string | null>(playlist.subject_id != null ? String(playlist.subject_id) : null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const current = playlist.subject_id != null ? String(playlist.subject_id) : null;
  const target = subjects.find(s => String(s.id) === subjectId);
  // Re-binding replaces that subject's playlist on this platform; say so before it happens.
  const replaces = subjectId !== current && target?.playlists?.some(l => l.platform === platform);

  async function save() {
    setSaving(true); setError(null);
    try {
      const patch: { title?: string; subject_id?: number | null } = {};
      if (title.trim() !== playlist.title) patch.title = title.trim();
      if (subjectId !== current) patch.subject_id = subjectId != null ? Number(subjectId) : null;
      if (Object.keys(patch).length) await updatePlaylist({ platform, channelId, playlistId: playlist.playlist_id, patch, fetch });
      onSaved();
    } catch (e) { setError(parseApiError(e).message); }
    finally { setSaving(false); }
  }

  return (
    <Modal opened onClose={onClose} title={<Anchor href={playlistUrl(platform, channelId, playlist.playlist_id)} target="_blank" rel="noopener noreferrer">{playlist.title}</Anchor>}>
      <Stack>
        <TextInput label="Название" description={`Меняется и в ${PLATFORM_TITLES[platform]}`} value={title} onChange={e => setTitle(e.target.value)} />
        <Select label="Предмет" placeholder="Не привязан" clearable searchable data={subjects.map(s => ({ value: String(s.id), label: s.title }))} value={subjectId} onChange={setSubjectId} />
        {replaces && <Text fz="xs" c="orange">У предмета «{target?.title}» уже есть плейлист в {PLATFORM_TITLES[platform]} — он будет отвязан.</Text>}
        {error && <Alert color="red">{error}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Отмена</Button>
          <Button loading={saving} disabled={!title.trim()} onClick={save}>Сохранить</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
